import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft, CheckCircle2, Languages, LoaderCircle, Send } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import type { AppUser } from '../../types';
import {
  submitFeedback,
  type FeedbackDevice,
  type FeedbackEase,
  type FeedbackPerformance,
  type FeedbackRecommendation
} from '../../backend/userRequestService';

type Language = 'en' | 'hi';

const text = (language: Language, english: string, hindi: string) =>
  language === 'hi' ? hindi : english;

const initialDevice = (): FeedbackDevice => {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') return 'Android';
  if (platform === 'ios') return 'iPhone';
  return 'Laptop/Desktop';
};

const initialVersion = () => typeof navigator === 'undefined' ? 'Web app' : navigator.userAgent.slice(0, 300);

const labelClass = 'block space-y-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200';

/** Text inputs use <label>. Button groups use <div role="group"> so clicking the question text never presses a button. */
function Field({ label, children, group = false }: { label: string; children: ReactNode; group?: boolean }) {
  if (group) {
    return <div role="group" aria-label={label} className={labelClass}>
      <span className="block">{label}</span>
      {children}
    </div>;
  }
  return <label className={labelClass}>
    <span>{label}</span>
    {children}
  </label>;
}

function SectionTitle({ number, children }: { number: number; children: ReactNode }) {
  return <div className="flex items-center gap-3 border-b border-slate-200 pb-3 dark:border-slate-800">
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-700 text-xs font-bold text-white">{number}</span>
    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">{children}</h2>
  </div>;
}

const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-blue-500';
const selectClass = `${inputClass} cursor-pointer`;

const choiceBase = 'rounded-lg border px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40';
const choiceOn = 'border-blue-700 bg-blue-50 text-blue-800 dark:border-blue-500 dark:bg-blue-950 dark:text-blue-200';
const choiceOff = 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900';

export function FeedbackPage({
  currentUser,
  onOpenAuth,
  onClose
}: {
  currentUser: AppUser | null;
  onOpenAuth: () => void;
  onClose: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [language, setLanguage] = useState<Language>('en');
  const [name, setName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [device, setDevice] = useState<FeedbackDevice>(initialDevice);
  const [otherDeviceDetails, setOtherDeviceDetails] = useState('');
  const [appVersion, setAppVersion] = useState(initialVersion);
  const [rating, setRating] = useState(0);
  const [encounteredBugs, setEncounteredBugs] = useState<boolean | null>(null);
  const [bugDescription, setBugDescription] = useState('');
  const [mostUsedFeature, setMostUsedFeature] = useState('');
  const [easeOfUse, setEaseOfUse] = useState<FeedbackEase | ''>('');
  const [confusingItems, setConfusingItems] = useState('');
  const [performance, setPerformance] = useState<FeedbackPerformance | ''>('');
  const [appCrashed, setAppCrashed] = useState<boolean | null>(null);
  const [crashDetails, setCrashDetails] = useState('');
  const [likedMost, setLikedMost] = useState('');
  const [improvements, setImprovements] = useState('');
  const [futureFeatures, setFutureFeatures] = useState('');
  const [recommendation, setRecommendation] = useState<FeedbackRecommendation | ''>('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!name && currentUser?.displayName) setName(currentUser.displayName);
    if (!email && currentUser?.email) setEmail(currentUser.email);
  }, [currentUser?.displayName, currentUser?.email, email, name]);

  // Always start at the top, and jump to top again after a successful submit.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [submitted]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError('');
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (rating === 0 || encounteredBugs === null || appCrashed === null || !easeOfUse || !performance || !recommendation) {
      setSubmitError(text(language, 'Please answer all required questions.', 'कृपया सभी आवश्यक सवालों के जवाब दें।'));
      return;
    }
    setIsSubmitting(true);
    try {
      await submitFeedback({
        name,
        email,
        device,
        otherDeviceDetails,
        appVersion,
        rating,
        encounteredBugs,
        bugDescription,
        mostUsedFeature,
        easeOfUse,
        confusingItems,
        performance,
        appCrashed,
        crashDetails,
        likedMost,
        improvements,
        futureFeatures,
        recommendation,
        additionalComments
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Feedback could not be sent.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const binaryChoices = (value: boolean | null, setValue: (next: boolean) => void) => (
    <div className="flex gap-2 pt-1">
      {([true, false] as const).map(option => (
        <button
          key={String(option)}
          type="button"
          aria-pressed={value === option}
          onClick={() => setValue(option)}
          className={`${choiceBase} min-w-[84px] ${value === option ? choiceOn : choiceOff}`}
        >
          {text(language, option ? 'Yes' : 'No', option ? 'हाँ' : 'नहीं')}
        </button>
      ))}
    </div>
  );

  const selectOption = (value: string, label: string) => <option key={value} value={value}>{label}</option>;

  const topBtn = 'inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  return (
    // Own scroll container: works even if the app root/body has overflow:hidden or a fixed height.
    <div
      ref={scrollRef}
      className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100"
      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
    >
      <header
        className="sticky top-0 z-10 border-b border-slate-200 bg-slate-100/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90"
        style={{ paddingTop: 'var(--sat)' }}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-3 py-3 sm:px-6">
          <button type="button" onClick={onClose} className={topBtn}>
            <ArrowLeft className="h-4 w-4" />{text(language, 'Back to YeTryKar', 'YeTryKar पर वापस जाएँ')}
          </button>
          <button type="button" onClick={() => setLanguage(value => value === 'en' ? 'hi' : 'en')} className={topBtn}>
            <Languages className="h-4 w-4" />{language === 'en' ? 'हिन्दी' : 'English'}
          </button>
        </div>
      </header>

      <main
        className="mx-auto max-w-3xl px-3 pt-5 sm:px-6"
        style={{ paddingBottom: 'calc(var(--sab) + 32px)' }}
      >
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-5 py-7 text-white sm:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300">YeTryKar Beta</p>
            <h1 className="mt-2 text-2xl font-black tracking-tight">{text(language, 'Beta Testing Feedback', 'बीटा टेस्टिंग फ़ीडबैक')}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-300">
              {text(language, 'Share honest feedback, report bugs, and help us improve YeTryKar before launch.', 'अपना सच्चा फ़ीडबैक दें, बग बताएँ और लॉन्च से पहले YeTryKar को बेहतर बनाने में मदद करें।')}
            </p>
          </div>

          <div className="p-5 sm:p-8">
            {submitted ? (
              <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
                <h2 className="mt-3 text-lg font-bold">{text(language, 'Thank you for testing YeTryKar!', 'YeTryKar को टेस्ट करने के लिए धन्यवाद!')}</h2>
                <p className="mt-2 text-sm">{text(language, 'Your feedback has been saved. You can email screenshots or screen recordings to support@yetrykar.com.', 'आपका फ़ीडबैक सुरक्षित कर लिया गया है। स्क्रीनशॉट या स्क्रीन रिकॉर्डिंग support@yetrykar.com पर ईमेल कर सकते हैं।')}</p>
                <button type="button" onClick={onClose} className="mt-5 rounded-lg bg-blue-700 px-4 py-2 text-sm font-bold text-white hover:bg-blue-800">{text(language, 'Return to app', 'ऐप पर वापस जाएँ')}</button>
              </div>
            ) : (
              <form onSubmit={event => void submit(event)} className="space-y-8">
                {!currentUser && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                    {text(language, 'Sign in to submit feedback securely.', 'फ़ीडबैक सुरक्षित रूप से भेजने के लिए साइन इन करें।')}
                    <button type="button" onClick={onOpenAuth} className="ml-2 font-bold underline">{text(language, 'Sign in', 'साइन इन')}</button>
                  </div>
                )}

                <section className="space-y-4">
                  <SectionTitle number={1}>{text(language, 'Basic information', 'बुनियादी जानकारी')}</SectionTitle>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={text(language, '1. Your name', '1. आपका नाम')}>
                      <input className={inputClass} maxLength={100} required value={name} onChange={event => setName(event.target.value)} />
                    </Field>
                    <Field label={text(language, '2. Email address', '2. ईमेल पता')}>
                      <input className={inputClass} type="email" maxLength={254} required value={email} onChange={event => setEmail(event.target.value)} />
                    </Field>
                    <Field label={text(language, '3. Device used', '3. इस्तेमाल किया गया डिवाइस')}>
                      <select className={selectClass} value={device} onChange={event => setDevice(event.target.value as FeedbackDevice)}>
                        {selectOption('Android', text(language, 'Android', 'Android'))}
                        {selectOption('iPhone', text(language, 'iPhone', 'iPhone'))}
                        {selectOption('Laptop/Desktop', text(language, 'Laptop/Desktop', 'लैपटॉप/डेस्कटॉप'))}
                        {selectOption('Other', text(language, 'Other', 'अन्य'))}
                      </select>
                    </Field>
                    {device === 'Other' && <Field label={text(language, 'Please specify your device', 'अपने डिवाइस का नाम बताएँ')}>
                      <input className={inputClass} required maxLength={100} value={otherDeviceDetails} onChange={event => setOtherDeviceDetails(event.target.value)} />
                    </Field>}
                    <Field label={text(language, '4. Browser / app version', '4. ब्राउज़र / ऐप वर्ज़न')}>
                      <input className={inputClass} maxLength={300} required value={appVersion} onChange={event => setAppVersion(event.target.value)} />
                    </Field>
                  </div>
                </section>

                <section className="space-y-4">
                  <SectionTitle number={2}>{text(language, 'Testing experience', 'टेस्टिंग का अनुभव')}</SectionTitle>
                  <Field group label={text(language, '5. Overall experience', '5. कुल अनुभव')}>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {[1, 2, 3, 4, 5].map(value => (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={rating === value}
                          aria-label={`${value} / 5`}
                          onClick={() => setRating(value)}
                          className={`${choiceBase} ${rating === value ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-300' : choiceOff}`}
                        >
                          {'★'.repeat(value)} <span className="ml-1">{value}</span>
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field group label={text(language, '6. Did you encounter bugs or errors?', '6. क्या आपको बग या त्रुटियाँ मिलीं?')}>
                    {binaryChoices(encounteredBugs, setEncounteredBugs)}
                  </Field>
                  {encounteredBugs && <Field label={text(language, 'If yes, please describe the issue', 'यदि हाँ, तो समस्या बताएँ')}>
                    <textarea className={inputClass} rows={3} maxLength={2000} required value={bugDescription} onChange={event => setBugDescription(event.target.value)} />
                  </Field>}
                  <Field label={text(language, '7. Which feature did you use most?', '7. आपने किस फ़ीचर का सबसे ज़्यादा उपयोग किया?')}>
                    <input className={inputClass} maxLength={200} value={mostUsedFeature} onChange={event => setMostUsedFeature(event.target.value)} />
                  </Field>
                  <Field label={text(language, '8. Was the app easy to use?', '8. क्या ऐप इस्तेमाल करना आसान था?')}>
                    <select className={selectClass} required value={easeOfUse} onChange={event => setEaseOfUse(event.target.value as FeedbackEase)}>
                      <option value="">{text(language, 'Choose one', 'एक विकल्प चुनें')}</option>
                      {selectOption('very_easy', text(language, 'Very easy', 'बहुत आसान'))}
                      {selectOption('easy', text(language, 'Easy', 'आसान'))}
                      {selectOption('neutral', text(language, 'Neutral', 'सामान्य'))}
                      {selectOption('difficult', text(language, 'Difficult', 'कठिन'))}
                      {selectOption('very_difficult', text(language, 'Very difficult', 'बहुत कठिन'))}
                    </select>
                  </Field>
                  <Field label={text(language, '9. Was anything confusing?', '9. क्या कोई चीज़ समझने में उलझन वाली थी?')}>
                    <textarea className={inputClass} rows={2} maxLength={1500} value={confusingItems} onChange={event => setConfusingItems(event.target.value)} />
                  </Field>
                </section>

                <section className="space-y-4">
                  <SectionTitle number={3}>{text(language, 'Performance and suggestions', 'परफ़ॉर्मेंस और सुझाव')}</SectionTitle>
                  <Field label={text(language, '10. How was app performance?', '10. ऐप की परफ़ॉर्मेंस कैसी थी?')}>
                    <select className={selectClass} required value={performance} onChange={event => setPerformance(event.target.value as FeedbackPerformance)}>
                      <option value="">{text(language, 'Choose one', 'एक विकल्प चुनें')}</option>
                      {selectOption('very_fast', text(language, 'Very fast', 'बहुत तेज़'))}
                      {selectOption('fast', text(language, 'Fast', 'तेज़'))}
                      {selectOption('average', text(language, 'Average', 'औसत'))}
                      {selectOption('slow', text(language, 'Slow', 'धीमी'))}
                    </select>
                  </Field>
                  <Field group label={text(language, '11. Did the app crash?', '11. क्या ऐप क्रैश हुआ?')}>
                    {binaryChoices(appCrashed, setAppCrashed)}
                  </Field>
                  {appCrashed && <Field label={text(language, '12. What were you doing when it happened?', '12. क्रैश होने पर आप क्या कर रहे थे?')}>
                    <textarea className={inputClass} rows={2} maxLength={1500} required value={crashDetails} onChange={event => setCrashDetails(event.target.value)} />
                  </Field>}
                  <Field label={text(language, '13. What did you like most?', '13. YeTryKar में आपको सबसे अच्छा क्या लगा?')}>
                    <textarea className={inputClass} rows={2} maxLength={1500} value={likedMost} onChange={event => setLikedMost(event.target.value)} />
                  </Field>
                  <Field label={text(language, '14. What improvements would you suggest?', '14. आप किन सुधारों का सुझाव देंगे?')}>
                    <textarea className={inputClass} rows={2} maxLength={1500} value={improvements} onChange={event => setImprovements(event.target.value)} />
                  </Field>
                  <Field label={text(language, '15. What feature should we add next?', '15. अगला कौन-सा फ़ीचर जोड़ा जाना चाहिए?')}>
                    <textarea className={inputClass} rows={2} maxLength={1500} value={futureFeatures} onChange={event => setFutureFeatures(event.target.value)} />
                  </Field>
                  <Field label={text(language, '16. Would you recommend YeTryKar?', '16. क्या आप YeTryKar की सिफ़ारिश करेंगे?')}>
                    <select className={selectClass} required value={recommendation} onChange={event => setRecommendation(event.target.value as FeedbackRecommendation)}>
                      <option value="">{text(language, 'Choose one', 'एक विकल्प चुनें')}</option>
                      {selectOption('yes', text(language, 'Yes', 'हाँ'))}
                      {selectOption('maybe', text(language, 'Maybe', 'शायद'))}
                      {selectOption('no', text(language, 'No', 'नहीं'))}
                    </select>
                  </Field>
                  <Field label={text(language, '17. Additional comments', '17. अतिरिक्त टिप्पणी')}>
                    <textarea className={inputClass} rows={3} maxLength={2000} value={additionalComments} onChange={event => setAdditionalComments(event.target.value)} />
                  </Field>
                </section>

                {submitError && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">{submitError}</p>}
                <button type="submit" disabled={isSubmitting || !currentUser} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {text(language, isSubmitting ? 'Sending…' : 'Send feedback', isSubmitting ? 'भेजा जा रहा है…' : 'फ़ीडबैक भेजें')}
                </button>
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">{text(language, 'Your response is stored securely and visible only to authorized YeTryKar administrators.', 'आपका जवाब सुरक्षित रखा जाता है और केवल अधिकृत YeTryKar एडमिन देख सकते हैं।')}</p>
              </form>
            )}
          </div>
        </section>
        <footer className="py-5 text-center text-xs text-slate-500 dark:text-slate-400">Team YeTryKar · <a className="font-semibold text-blue-700 underline dark:text-blue-400" href="mailto:support@yetrykar.com">support@yetrykar.com</a></footer>
      </main>
    </div>
  );
}